import { Controller } from "@hotwired/stimulus"

// Handles the snooze/reschedule menu for tasks
export default class extends Controller {
  static targets = ["menu", "datePicker", "dateInput"]
  static values = {
    taskId: Number,
    rescheduleUrl: String,
    taskDate: String,  // Current task date (YYYY-MM-DD)
    startDate: String, // Current view's start date for redirect context
    recurring: Boolean // Whether this is a recurring task
  }

  connect() {
    // Close menu when clicking outside
    this.boundCloseOnOutsideClick = this.closeOnOutsideClick.bind(this)
    document.addEventListener("click", this.boundCloseOnOutsideClick)
  }

  disconnect() {
    document.removeEventListener("click", this.boundCloseOnOutsideClick)
  }

  toggle(event) {
    event.stopPropagation()
    this.menuTarget.classList.toggle("hidden")
    if (this.hasDatePickerTarget) {
      this.datePickerTarget.classList.add("hidden")
    }
  }

  close() {
    this.menuTarget.classList.add("hidden")
    if (this.hasDatePickerTarget) {
      this.datePickerTarget.classList.add("hidden")
    }
  }

  closeOnOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  // Quick reschedule options
  tomorrow() {
    const date = this.addDays(this.parseTaskDate(), 1)
    this.reschedule(date)
  }

  inTwoDays() {
    const date = this.addDays(this.parseTaskDate(), 2)
    this.reschedule(date)
  }

  nextWeek() {
    const date = this.addDays(this.parseTaskDate(), 7)
    this.reschedule(date)
  }

  // Show date picker
  showDatePicker(event) {
    event.stopPropagation()
    this.menuTarget.classList.add("hidden")
    this.datePickerTarget.classList.remove("hidden")

    // Set minimum date to today
    const today = new Date().toISOString().split('T')[0]
    this.dateInputTarget.min = today
    this.dateInputTarget.value = ""
    this.dateInputTarget.focus()
  }

  // Handle custom date selection
  selectDate() {
    const dateValue = this.dateInputTarget.value
    if (dateValue) {
      this.reschedule(dateValue)
    }
  }

  cancelDatePicker() {
    this.datePickerTarget.classList.add("hidden")
  }

  // Helper to parse task date
  parseTaskDate() {
    return new Date(this.taskDateValue)
  }

  // Helper to add days to a date
  addDays(date, days) {
    const result = new Date(date)
    result.setDate(result.getDate() + days)
    return result.toISOString().split('T')[0]
  }

  // Submit reschedule request
  reschedule(targetDate) {
    this.close()

    // Calculate day shift for recurring tasks
    if (this.recurringValue) {
      const oldDate = new Date(this.taskDateValue)
      const newDate = new Date(targetDate)
      const dayShift = Math.round((newDate - oldDate) / (1000 * 60 * 60 * 24))

      if (dayShift !== 0) {
        const shiftDirection = dayShift > 0 ? `+${dayShift}` : `${dayShift}`
        const message = `Shift all future recurring tasks by ${shiftDirection} day${Math.abs(dayShift) !== 1 ? 's' : ''} too?`

        if (confirm(message)) {
          this.doReschedule(targetDate, true)
          return
        }
      }
    }

    this.doReschedule(targetDate, false)
  }

  doReschedule(targetDate, shiftFuture) {
    const formData = new FormData()
    formData.append("target_date", targetDate)
    formData.append("start_date", this.startDateValue)
    if (shiftFuture) {
      formData.append("shift_future", "true")
    }

    fetch(this.rescheduleUrlValue, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        "Accept": "text/vnd.turbo-stream.html"
      },
      body: formData
    })
    .then(response => {
      if (!response.ok) {
        console.error("Reschedule failed:", response.status)
        return
      }
      return response.text()
    })
    .then(html => {
      if (html) {
        Turbo.renderStreamMessage(html)
      }
    })
    .catch(error => {
      console.error("Reschedule error:", error)
    })
  }
}
